/**
 * Decode JWT payload (middle segment) without verifying the signature.
 * Use only for UI / client-side context; authorization remains on the API.
 */
export function decodeJwtPayload(accessToken) {
  if (!accessToken || typeof accessToken !== 'string') {
    return null;
  }
  const parts = accessToken.split('.');
  if (parts.length < 2) {
    return null;
  }
  try {
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Map JWT payload claims to the app user object.
 *
 * Matches claims issued in IdentityServer CustomResourceOwnerPasswordValidator:
 * - Claim "username"   → userData.UserName
 * - Claim "email"      → userData.Email
 * - Claim "enUserType" → userData.EnUserType (string in token)
 * - Grant subject      → JWT "sub" (validator uses context.UserName)
 *
 * Duende may emit additional standard claims; unknown keys are ignored.
 */
export function buildUserFromClaims(claims, loginUsernameFallback) {
  if (!claims || typeof claims !== 'object') {
    return null;
  }
  const pickString = (value) => {
    if (value == null) {
      return null;
    }
    if (Array.isArray(value)) {
      return value.length ? String(value[0]) : null;
    }
    return String(value);
  };

  const username =
    pickString(claims.username) ??
    pickString(claims.sub) ??
    loginUsernameFallback ??
    '';

  const rawEnUserType = pickString(claims.enUserType);
  let enUserType = null;
  if (rawEnUserType != null && rawEnUserType !== '') {
    const n = Number(rawEnUserType);
    enUserType = Number.isNaN(n) ? rawEnUserType : n;
  }

  const email = pickString(claims.email) || null;
  const sub = pickString(claims.sub) || null;

  return {
    username,
    email,
    enUserType,
    sub,
  };
}
